import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Search as FiSearch } from 'lucide-react';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import StatusBadge from '../components/StatusBadge';
import LoadingState from '../components/LoadingState';
import {
  AccountsPayableRecord,
  AccountsPayablePaymentRecord,
  AccountsPayableListResponse,
  listAccountsPayablePayments,
  listAccountsPayables,
  syncAccountsPayables,
  syncDuplicatesNote,
} from '../lib/accountsPayableApi';
import { formatDate as fmtDate, storedDay } from "../utils/dateFormat";
import { downloadObjectsCsv } from '../utils/downloadCsv';
import SupplierAging from '../features/accountsPayableReports/SupplierAging';
// One line per currency, never the server's converted single total (D49).
import { CurrencyLines } from '../features/accountsPayableReports/CurrencyLines';
// A bill's money: TT$ / US$ from the label owner, never the locale's bare "$" (lane 5, V3d).
import { formatBillMoney as formatMoney } from '../features/accountsPayableReports/format';

// Invoice, due and payment dates are stored CALENDAR DAYS (UTC midnight): print their own day (D109.6).
function formatStoredDay(value?: string | null) {
  return fmtDate(storedDay(value), '--');
}


const INFO = (
  <div className="space-y-2">
    <p>This supplier detail page groups the AP records for one supplier.</p>
    <p>Use it to review totals, overdue invoices, and aging buckets.</p>
  </div>
);

export const AccountsSupplierDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { supplierName: supplierNameParam } = useParams();
  // The router has already decoded the param. Decoding it again threw a URIError for a
  // supplier whose name contains a "%" (found reviewing D49 phase 3).
  const supplierName = supplierNameParam || '';

  const [rows, setRows] = useState<AccountsPayableRecord[]>([]);
  const [payments, setPayments] = useState<AccountsPayablePaymentRecord[]>([]);
  const [summary, setSummary] = useState<AccountsPayableListResponse['summary']>({
    invoiceCount: 0,
    totalAmount: 0,
    paidAmount: 0,
    outstandingAmount: 0,
    unpaidCount: 0,
    partialCount: 0,
    paidCount: 0,
    overdueCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  // What the last Sync has to say beyond "done" (D116.3: confirmed invoices it gave no bill).
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setPaymentsLoading(true);
    try {
      const [apRes, paymentRes] = await Promise.all([
        listAccountsPayables({
          page: 1,
          limit: 200,
          supplier: supplierName,
          q: search.trim(),
        }),
        // D49 phase 5: live payments only; a voided one is history, not money paid.
        listAccountsPayablePayments({
          page: 1,
          limit: 100,
          supplier: supplierName,
          q: search.trim(),
          status: 'applied',
        }),
      ]);
      const data = apRes.data;
      const paymentData = paymentRes.data;
      setRows(data.items || []);
      setPayments(paymentData.items || []);
      setSummary(
        data.summary || {
          invoiceCount: 0,
          totalAmount: 0,
          paidAmount: 0,
          outstandingAmount: 0,
          unpaidCount: 0,
          partialCount: 0,
          paidCount: 0,
          overdueCount: 0,
        }
      );
    } catch {
      setRows([]);
      setPayments([]);
    } finally {
      setLoading(false);
      setPaymentsLoading(false);
    }
  };

  const syncAndLoad = async () => {
    setSyncing(true);
    setSyncNote(null);
    try {
      const res = await syncAccountsPayables(true);
      setSyncNote(syncDuplicatesNote(res?.data));
      await load();
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierName]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void load();
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const exportRows = useMemo(
    () =>
      rows.map((row) => ({
        'Invoice #': row.invoiceNumber || '',
        Supplier: row.supplierName || '',
        Contact: row.supplierContact || '',
        'Invoice Date': formatStoredDay(row.invoiceDate),
        'Due Date': formatStoredDay(row.dueDate),
        Currency: row.currency || 'TTD',
        'Total Amount': row.totalAmount ?? 0,
        'Paid Amount': row.paidAmount ?? 0,
        'Outstanding Amount': row.outstandingAmount ?? 0,
        Status: row.status,
      })),
    [rows]
  );

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      <div className="px-8 pt-4 pb-3 bg-brand-bg border-b border-slate-200">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/accounts')}
              className="inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900"
            >
              <ArrowLeft aria-hidden="true" className="h-4 w-4" />
              Back
            </button>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold leading-7 text-slate-900">{supplierName || 'Supplier'}</h1>
              <ModuleInfoButton content={INFO} ariaLabel="Supplier AP information" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search here"
                aria-label="Search this supplier's bills"
                className="h-10 w-64 rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-cyan-300"
              />
            </div>

            <button
              onClick={syncAndLoad}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-cyan-300 bg-white text-cyan-700 text-sm font-semibold hover:bg-cyan-50 disabled:opacity-50"
              disabled={syncing}
            >
              <span className={syncing ? 'animate-spin' : ''}>↻</span>
              {syncing ? 'Syncing...' : 'Sync'}
            </button>

            <button
              onClick={() => downloadObjectsCsv(`${supplierName || 'supplier'}-ap.csv`, exportRows)}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600"
            >
              Export CSV
            </button>
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Supplier-level AP detail is derived from vendor invoices already tracked in Accounts Payable.
        </p>
      </div>

      <div className="px-8 py-4 flex-1 overflow-auto">
        {syncNote ? (
          <div role="status" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {syncNote}
          </div>
        ) : null}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
          <Card label="Invoices" value={summary.invoiceCount} />
          <Card label="Total Billed" value={<CurrencyLines byCurrency={summary.byCurrency} field="totalAmount" />} />
          <Card label="Paid" value={<CurrencyLines byCurrency={summary.byCurrency} field="paidAmount" />} />
          <Card label="Outstanding" value={<CurrencyLines byCurrency={summary.byCurrency} field="outstandingAmount" />} />
        </div>

        {/* D49 phase 2: the SERVER's aging for this supplier, one table per currency. It replaced
            a version that added TTD and USD into one figure and only saw the first 200 bills. */}
        <SupplierAging supplierName={supplierName} />

        <div className="grid grid-cols-1 gap-4 mb-4">
          <div className="rounded-xl bg-white border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">Supplier Statement</div>
                <div className="text-xs text-slate-500">
                  Opening balance, invoices, payments and credits with a running balance, for{' '}
                  {supplierName || 'this supplier'}. Printable, and exportable to CSV.
                </div>
              </div>
              {/* D49 phase 3: the statement of account has its own page. This replaced a CSV built here
                  from unsorted rows that mixed currencies and had no balance. */}
              <button
                onClick={() => navigate(`/accounts/suppliers/${encodeURIComponent(supplierName)}/statement`)}
                disabled={!supplierName}
                className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Open statement
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Card label="Invoices" value={summary.invoiceCount} />
              <Card label="Payments" value={payments.length} />
            </div>
          </div>

        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="overflow-auto">
            <table className="data-table data-table-grid min-w-[1200px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs text-slate-600">
                  <th className="px-4 py-3">Invoice #</th>
                  <th className="px-4 py-3">Invoice Date</th>
                  <th className="px-4 py-3">Due Date</th>
                  <th className="px-4 py-3">Total Amount</th>
                  <th className="px-4 py-3">Paid Amount</th>
                  <th className="px-4 py-3">Outstanding</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Notes</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10">
                      <LoadingState text="Loading supplier statement..." />
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                      No invoices found for this supplier.
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row._id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">
                      <button
                        onClick={() => navigate(`/accounts/${row._id}`)}
                        className="text-left text-cyan-700 hover:underline"
                        title="Open invoice detail"
                      >
                        {row.invoiceNumber || '--'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{formatStoredDay(row.invoiceDate)}</td>
                    <td className="px-4 py-3 text-slate-700">{formatStoredDay(row.dueDate)}</td>
                    <td className="px-4 py-3 text-slate-700">{formatMoney(row.totalAmount, row.currency || 'TTD')}</td>
                    <td className="px-4 py-3 text-slate-700">{formatMoney(row.paidAmount, row.currency || 'TTD')}</td>
                    <td className="px-4 py-3 text-slate-700">{formatMoney(row.outstandingAmount, row.currency || 'TTD')}</td>
                    <td className="px-4 py-3">{row.status ? <StatusBadge status={String(row.status)} /> : '--'}</td>
                    <td className="px-4 py-3 text-slate-700">{row.notes || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-800">Payment History</div>
              <div className="text-xs text-slate-500">Payments recorded against this supplier.</div>
            </div>
          </div>
          <div className="overflow-auto">
            <table className="data-table data-table-grid min-w-[900px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs text-slate-600">
                  <th className="px-4 py-3">Reference</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Allocated</th>
                  <th className="px-4 py-3">Unallocated</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Voucher</th>
                </tr>
              </thead>
              <tbody>
                {paymentsLoading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      Loading payments...
                    </td>
                  </tr>
                )}
                {!paymentsLoading && payments.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No payments found for this supplier.
                    </td>
                  </tr>
                )}
                {!paymentsLoading &&
                  payments.map((row) => (
                    <tr key={row._id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{row.reference || '--'}</td>
                      <td className="px-4 py-3 text-slate-700">{formatStoredDay(row.paymentDate)}</td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {formatMoney(row.amount, row.currency || 'TTD')}
                      </td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {formatMoney(
                          row.allocations?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0,
                          row.currency || 'TTD'
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {formatMoney(row.unallocatedAmount, row.currency || 'TTD')}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{row.method || '--'}</td>
                      <td className="px-4 py-3 text-slate-700">
                        <button
                          onClick={() => navigate(`/accounts/payments/${row._id}`)}
                          className="px-3 py-1.5 rounded-md border border-cyan-300 bg-white text-xs font-semibold text-cyan-700 hover:bg-cyan-50"
                        >
                          Create Voucher
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

function Card({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}
